// 右ペイン（シナリオプレビュー / JSON / バリデーションバナー）の描画
import type { Scenario } from '../core/scenario.js'
import { validateScenario } from '../core/validation.js'

const validationBanner = document.getElementById('validation-banner') as HTMLDivElement
const tabPreview = document.getElementById('tab-preview') as HTMLDivElement
const tabJson = document.getElementById('tab-json') as HTMLPreElement
const copyBtn = document.getElementById('copy-btn') as HTMLButtonElement
const downloadBtn = document.getElementById('download-btn') as HTMLButtonElement

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function renderValidation(scenario: Scenario | null): void {
  if (!scenario) {
    validationBanner.hidden = true
    return
  }
  const validation = validateScenario(scenario)
  validationBanner.hidden = false
  if (validation.valid) {
    validationBanner.className = 'validation-banner ok'
    validationBanner.textContent = '✓ 有効なシナリオです（そのまま拡張機能にインポートできます）'
  } else {
    validationBanner.className = 'validation-banner ng'
    validationBanner.textContent =
      `⚠ バリデーションエラー ${validation.errors.length}件\n` +
      validation.errors.map((e) => `・${e.path}: ${e.message}`).join('\n')
  }
}

function renderStepDetail(dl: HTMLElement, label: string, value: string): void {
  dl.appendChild(el('dt', undefined, label))
  dl.appendChild(el('dd', undefined, value))
}

function renderEmptyState(): void {
  const empty = el('div', 'scenario-empty')
  empty.append(
    'まだシナリオがありません。',
    document.createElement('br'),
    'チャットで作成するか、JSONをインポートしてください。',
    document.createElement('br'),
  )
  const hint = el('span', 'scenario-empty-hint')
  hint.append('作成したシナリオは ')
  const link = document.createElement('a')
  link.href = '/extension.html'
  link.textContent = 'Manual E2E Test Assistant（Chrome拡張）'
  hint.append(link, ' にインポートして使います。')
  empty.appendChild(hint)
  tabPreview.appendChild(empty)
}

export function renderScenarioPane(scenario: Scenario | null): void {
  renderValidation(scenario)

  // 全再構築でスクロール位置が先頭に戻らないよう、描画前の位置を復元する
  const jsonScrollTop = tabJson.scrollTop
  tabJson.textContent = scenario ? JSON.stringify(scenario, null, 2) : ''
  tabJson.scrollTop = jsonScrollTop

  copyBtn.disabled = !scenario
  downloadBtn.disabled = !scenario

  const previewScrollTop = tabPreview.scrollTop
  tabPreview.innerHTML = ''
  if (!scenario) {
    renderEmptyState()
    return
  }

  const header = el('div', 'sc-header')
  header.appendChild(el('h2', undefined, scenario.name))
  const meta = el('div', 'sc-meta')
  meta.appendChild(el('span', undefined, `ID: ${scenario.id}`))
  meta.appendChild(el('span', undefined, `v${scenario.version}`))
  if (scenario.targetUrl) meta.appendChild(el('span', undefined, scenario.targetUrl))
  const stepCount = (scenario.categories || []).reduce((n, c) => n + (c.steps || []).length, 0)
  meta.appendChild(
    el('span', undefined, `${(scenario.categories || []).length}カテゴリ / ${stepCount}ステップ`),
  )
  header.appendChild(meta)
  if (scenario.description) header.appendChild(el('p', 'sc-desc', scenario.description))
  tabPreview.appendChild(header)

  for (const cat of scenario.categories || []) {
    const catDiv = el('div', 'sc-category')
    const catHeader = el('div', 'sc-category-header')
    const h3 = el('h3', undefined, cat.name)
    h3.appendChild(el('span', 'cat-id', cat.id))
    catHeader.appendChild(h3)
    if (cat.description) catHeader.appendChild(el('p', undefined, cat.description))
    catDiv.appendChild(catHeader)

    for (const step of cat.steps || []) {
      const stepDiv = el('div', 'sc-step')
      const title = el('div', 'sc-step-title')
      title.appendChild(el('span', 'step-id', step.id))
      title.appendChild(el('span', undefined, step.title))
      if (step.tags && step.tags.length) {
        const chips = el('span', 'chips')
        for (const tag of step.tags) chips.appendChild(el('span', 'chip', tag))
        title.appendChild(chips)
      }
      stepDiv.appendChild(title)

      const dl = el('dl')
      renderStepDetail(dl, '手順', step.instruction)
      renderStepDetail(dl, '期待結果', step.expected)
      if (step.tips) renderStepDetail(dl, 'ヒント', step.tips)
      if (step.url) renderStepDetail(dl, 'URL', step.url)
      if (step.dependsOn && step.dependsOn.length) {
        renderStepDetail(dl, '依存', step.dependsOn.join(', '))
      }
      if (step.inputData && Object.keys(step.inputData).length) {
        dl.appendChild(el('dt', undefined, '入力値'))
        const dd = el('dd')
        for (const [key, value] of Object.entries(step.inputData)) {
          dd.appendChild(el('span', 'kv', `${key}: ${JSON.stringify(value)}`))
        }
        dl.appendChild(dd)
      }
      stepDiv.appendChild(dl)
      catDiv.appendChild(stepDiv)
    }
    tabPreview.appendChild(catDiv)
  }

  tabPreview.scrollTop = previewScrollTop
}

export function initScenarioPane(getScenario: () => Scenario | null): void {
  copyBtn.addEventListener('click', async () => {
    const scenario = getScenario()
    if (!scenario) return
    await navigator.clipboard.writeText(JSON.stringify(scenario, null, 2))
    copyBtn.textContent = 'コピーしました'
    setTimeout(() => (copyBtn.textContent = 'コピー'), 1500)
  })

  downloadBtn.addEventListener('click', () => {
    const scenario = getScenario()
    if (!scenario) return
    const blob = new Blob([JSON.stringify(scenario, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${scenario.id || 'scenario'}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  })

  document.querySelectorAll<HTMLButtonElement>('.tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.tab').forEach((t) => t.classList.remove('active'))
      tab.classList.add('active')
      const isPreview = tab.dataset.tab === 'preview'
      tabPreview.hidden = !isPreview
      tabJson.hidden = isPreview
    })
  })
}
